<template>
  <div class="club-payments-setup">
    <spinner-loader
      :loadingStuff="loadingStatus"
      loadIngStuffText="Loading Club Payments"
    />
    <h2>Club Payments</h2>

    <!-- Connect status ------------------------------------------------- -->
    <div class="payments-card">
      <div class="card-title">
        <SectionIcon name="stripeAccount" size="40" />
        <h3>Stripe account</h3>
      </div>

      <div v-if="loadingStatus"><p>Checking Stripe account…</p></div>

      <div v-else-if="!connectStatus.connected">
        <p>
          Connect a Stripe account so your club can collect registration and
          membership fees. Money goes directly to the club's own Stripe
          account and is paid out to the club bank account.
        </p>
        <button class="green-button" :disabled="creatingLink" @click="startOnboarding">
          {{ creatingLink ? "Opening Stripe…" : "Connect with Stripe" }}
        </button>
      </div>

      <div v-else>
        <p>
          <span class="status-pill" :class="connectStatus.charges_enabled ? 'status-good' : 'status-warn'">
            {{ connectStatus.charges_enabled ? "Ready to take payments" : "Onboarding incomplete" }}
          </span>
        </p>
        <ul class="onboarding-checklist">
          <li :class="connectStatus.details_submitted ? 'done' : 'pending'">
            <i class="bi" :class="connectStatus.details_submitted ? 'bi-check-circle-fill' : 'bi-circle'"></i>
            Business details submitted
          </li>
          <li :class="connectStatus.charges_enabled ? 'done' : 'pending'">
            <i class="bi" :class="connectStatus.charges_enabled ? 'bi-check-circle-fill' : 'bi-circle'"></i>
            Able to take payments
          </li>
          <li :class="connectStatus.payouts_enabled ? 'done' : 'pending'">
            <i class="bi" :class="connectStatus.payouts_enabled ? 'bi-check-circle-fill' : 'bi-circle'"></i>
            Payouts to bank enabled
          </li>
        </ul>
        <p class="muted">Account: {{ connectStatus.stripe_account_id }}</p>
        <p class="muted" v-if="connectStatus.application_fee_percent">
          Platform fee: {{ connectStatus.application_fee_percent }}% per transaction
          (plus Stripe's own processing fee).
        </p>
        <p class="muted" v-if="!connectStatus.charges_enabled">
          Stripe needs a little more information before the club can take
          payments — the fee plans and fundraising sections will reappear
          here once onboarding is complete.
        </p>
        <button
          v-if="!connectStatus.charges_enabled"
          class="green-button"
          :disabled="creatingLink"
          @click="startOnboarding"
        >
          {{ creatingLink ? "Opening Stripe…" : "Continue Stripe onboarding" }}
        </button>
      </div>

      <p v-if="statusError" class="error-text">{{ statusError }}</p>
    </div>

    <!-- Fee plans -------------------------------------------------------- -->
    <div class="payments-card" v-if="connectStatus.connected && connectStatus.charges_enabled">
      <div class="card-title with-action">
        <div class="card-title-left">
          <SectionIcon name="feePlans" size="40" />
          <h3>Membership fee plans</h3>
        </div>
        <button class="action-chip" @click="showPlanForm = !showPlanForm">
          <i class="bi" :class="showPlanForm ? 'bi-x-lg' : 'bi-plus-lg'"></i>
          {{ showPlanForm ? "Close" : "Add plan" }}
        </button>
      </div>
      <p class="muted">
        Plans are offered to new members on the registration page. Deactivated
        plans stop being offered but existing subscriptions continue.
      </p>

      <div class="inline-form plan-form-panel" v-if="showPlanForm">
        <label class="grow">
          Name
          <input type="text" v-model="newPlan.name" placeholder="e.g. Youth membership" />
        </label>
        <label class="grow">
          Description (optional)
          <input type="text" v-model="newPlan.description" />
        </label>
        <label>
          Amount ({{ currencySymbol }})
          <input type="number" min="1" step="0.01" v-model="newPlan.amountPounds" />
        </label>
        <label>
          Billing
          <select v-model="newPlan.billing_interval">
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
            <option value="one_off">One-off</option>
            <option value="installments">Installments (deposit + balance)</option>
          </select>
        </label>

        <div v-if="newPlan.billing_interval === 'installments'" class="installments-editor">
          <p class="muted">
            The amount above is the season total. Leave one installment's
            amount blank and it becomes the remainder automatically (after
            any vouchers or part-payments).
          </p>
          <div
            v-for="(inst, idx) in newPlan.installments"
            :key="idx"
            class="inline-form installment-row"
          >
            <label>
              Label
              <input type="text" v-model="inst.label" placeholder="e.g. Deposit" />
            </label>
            <label>
              Amount ({{ currencySymbol }}, blank = remainder)
              <input type="number" min="0.01" step="0.01" v-model="inst.amountPounds" />
            </label>
            <label>
              Due date
              <input type="date" v-model="inst.due_date" />
            </label>
            <button
              v-if="newPlan.installments.length > 1"
              class="link-button danger"
              @click="newPlan.installments.splice(idx, 1)"
            >
              Remove
            </button>
          </div>
          <button
            class="link-button"
            @click="newPlan.installments.push({ label: '', amountPounds: null, due_date: '' })"
          >
            + Add installment
          </button>
        </div>

        <button class="action-chip confirm" :disabled="savingPlan" @click="savePlan">
          {{ savingPlan ? "Saving…" : "Add plan" }}
        </button>
        <p v-if="planError" class="error-text">{{ planError }}</p>
      </div>

      <table class="payments-table" v-if="feePlans.length > 0">
        <thead>
          <tr>
            <th>Name</th>
            <th>Amount</th>
            <th>Billing</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="plan in feePlans" :key="plan.id">
            <td>{{ plan.name }}</td>
            <td>{{ pounds(plan.amount_pence) }}</td>
            <td>{{ intervalLabel(plan.billing_interval, plan) }}</td>
            <td>{{ plan.active == 1 ? "Active" : "Inactive" }}</td>
            <td>
              <button
                v-if="plan.active == 1"
                class="link-button"
                @click="deactivatePlan(plan)"
              >
                Deactivate
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="muted">No fee plans yet.</p>

    </div>

    <!-- Sports voucher claims (tenant-gated: AU clubs) -------------------- -->
    <ClubVouchers
      v-if="connectStatus.connected && voucherScheme"
      :club-id="userData.clubId"
    />

    <!-- Fundraising campaigns -------------------------------------------- -->
    <ClubFundraising
      v-if="connectStatus.connected && connectStatus.charges_enabled"
      :club-id="userData.clubId"
    />

    <!-- Members payment status (treasurer view) -------------------------- -->
    <MembersPayments
      v-if="connectStatus.connected"
      :club-id="userData.clubId"
      :active-plans="activePlans"
    />

    <!-- The club's own subscription to the platform (grassroots only; the
         component hides itself for tenants with no per-player billing). -->
    <ClubPlatformBilling :club-id="userData.clubId" />
  </div>
</template>

<script>
import {
  getPaymentsCurrency,
  getCurrencySymbol,
  getVoucherConfig,
} from "@/config/tenant";
import { StripeConnect } from "@/services/stripe-connect-service.js";
import MembersPayments from "./MembersPayments.vue";
import ClubFundraising from "./Fundraising.vue";
import ClubPlatformBilling from "./PlatformBilling.vue";
import ClubVouchers from "./Vouchers.vue";
import SectionIcon from "@/components/SectionIcon/index.vue";
import SpinnerLoader from "@/components/LoadingSpinning/index.vue";

export default {
  name: "ClubPaymentsSetup",
  components: {
    MembersPayments,
    ClubFundraising,
    ClubPlatformBilling,
    ClubVouchers,
    SectionIcon,
    SpinnerLoader,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingStatus: true,
      creatingLink: false,
      statusError: "",
      connectStatus: { connected: false },
      feePlans: [],
      newPlan: {
        name: "",
        description: "",
        amountPounds: null,
        billing_interval: "month",
        installments: [
          { label: "Deposit", amountPounds: null, due_date: "" },
          { label: "Balance", amountPounds: null, due_date: "" },
        ],
      },
      savingPlan: false,
      planError: "",
      showPlanForm: false,
    };
  },
  computed: {
    currencySymbol() {
      return getCurrencySymbol();
    },
    activePlans() {
      return this.feePlans.filter((plan) => plan.active == 1);
    },
    voucherScheme() {
      return getVoucherConfig();
    },
  },
  created() {
    this.loadStatus();
  },
  methods: {
    pounds(amountPence) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: getPaymentsCurrency(),
      }).format((amountPence || 0) / 100);
    },
    intervalLabel(interval, plan = null) {
      if (plan && plan.plan_type === "installments") {
        const labels = (plan.installments || []).map((i) => i.label);
        return labels.length ? `Installments (${labels.join(" + ")})` : "Installments";
      }
      if (interval === "month") return "Monthly";
      if (interval === "year") return "Yearly";
      return "One-off";
    },
    async loadStatus() {
      this.loadingStatus = true;
      this.statusError = "";
      try {
        const response = await StripeConnect.getConnectStatus(
          this.userData.clubId,
        );
        this.connectStatus = response.data.data || { connected: false };
        if (this.connectStatus.connected) {
          this.loadFeePlans();
        }
      } catch (error) {
        this.statusError =
          error?.response?.data?.error || "Could not load Stripe status.";
      } finally {
        this.loadingStatus = false;
      }
    },
    async startOnboarding() {
      this.creatingLink = true;
      this.statusError = "";
      try {
        const response = await StripeConnect.createOnboardingLink(
          this.userData.clubId,
          this.userData.email,
        );
        window.location.href = response.data.data.url;
      } catch (error) {
        this.statusError =
          error?.response?.data?.error || "Could not start Stripe onboarding.";
        this.creatingLink = false;
      }
    },
    async loadFeePlans() {
      try {
        const response = await StripeConnect.listFeePlans(
          this.userData.clubId,
          true,
        );
        this.feePlans = response.data.data || [];
      } catch (error) {
        console.log(error);
      }
    },
    async savePlan() {
      this.planError = "";
      const amountPence = Math.round(
        parseFloat(this.newPlan.amountPounds || 0) * 100,
      );
      const isInstallments = this.newPlan.billing_interval === "installments";
      const payload = {
        clubId: this.userData.clubId,
        name: this.newPlan.name,
        description: this.newPlan.description,
        amount_pence: amountPence,
        billing_interval: isInstallments ? "one_off" : this.newPlan.billing_interval,
      };
      if (isInstallments) {
        payload.plan_type = "installments";
        payload.installments = this.newPlan.installments.map((inst) => ({
          label: inst.label,
          amount_pence:
            inst.amountPounds !== null && inst.amountPounds !== ""
              ? Math.round(parseFloat(inst.amountPounds) * 100)
              : null,
          due_date: inst.due_date || null,
        }));
      }
      this.savingPlan = true;
      try {
        await StripeConnect.saveFeePlan(payload);
        this.newPlan = {
          name: "",
          description: "",
          amountPounds: null,
          billing_interval: "month",
          installments: [
            { label: "Deposit", amountPounds: null, due_date: "" },
            { label: "Balance", amountPounds: null, due_date: "" },
          ],
        };
        this.showPlanForm = false;
        this.loadFeePlans();
      } catch (error) {
        this.planError =
          error?.response?.data?.error || "Could not save the plan.";
      } finally {
        this.savingPlan = false;
      }
    },
    async deactivatePlan(plan) {
      try {
        await StripeConnect.deactivateFeePlan(this.userData.clubId, plan.id);
        this.loadFeePlans();
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.club-payments-setup {
  .payments-card {
    background: #fff;
    border: 1px solid #e3e3e3;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;

    h3 {
      margin: 0;
    }
  }

  .card-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;

    &.with-action {
      justify-content: space-between;
    }

    .card-title-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
  }

  .action-chip {
    border: 1px solid #ccc;
    background: #fff;
    color: #444;
    border-radius: 6px;
    padding: 0.35rem 0.8rem;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    i {
      margin-right: 0.3rem;
    }

    &:hover:not(:disabled) {
      background: #e8f2fd;
      border-color: #1e87f0;
      color: #14508f;
    }

    &.confirm {
      background: #1e87f0;
      border-color: #1e87f0;
      color: #fff;

      &:hover:not(:disabled) {
        background: #1a78d6;
        color: #fff;
      }
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .inline-form.plan-form-panel {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
    margin: 0.5rem 0 1rem;
    padding: 0.85rem;
    background: #f4f7fb;
    border: 1px solid #dbe6f3;
    border-radius: 8px;

    label {
      display: flex;
      flex-direction: column;
      font-size: 0.75rem;
      color: #666;
      gap: 0.2rem;

      &.grow {
        flex: 1 1 220px;
      }

      input,
      select {
        padding: 0.4rem 0.55rem;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 0.9rem;
        color: #333;
        background: #fff;
        width: auto;
      }
    }
  }

  .card-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .muted {
    color: #777;
    font-size: 0.9rem;
  }

  .error-text {
    color: #c0392b;
  }

  .payments-table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75rem 0;

    th,
    td {
      text-align: left;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid #eee;
    }
  }

  .status-pill {
    display: inline-block;
    padding: 0.15rem 0.6rem;
    border-radius: 999px;
    font-size: 0.8rem;

    &.status-good {
      background: #e6f6ec;
      color: #1e7b3c;
    }
    &.status-warn {
      background: #fdf3e0;
      color: #9a6b13;
    }
    &.status-bad {
      background: #fdeaea;
      color: #b03030;
    }
  }

  .onboarding-checklist {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0 0.75rem;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9rem;
      padding: 0.15rem 0;

      i {
        font-size: 1rem;
      }

      &.done {
        color: #1e7b3c;
      }
      &.pending {
        color: #8a8f98;
      }
    }
  }

  .link-button {
    background: none;
    border: none;
    color: #1e87f0;
    cursor: pointer;
    padding: 0;
    font-size: 0.9rem;

    &:disabled {
      color: #999;
      cursor: default;
    }
  }

}
</style>
